import React, { Component, ErrorInfo, ReactNode } from 'react';
import { useFlowObserverPanelStore } from '@flow-studio/flow-observer-core';
import type { FlowObserverTabContentProps } from '@flow-studio/flow-observer-core';

// ── Error boundary ────────────────────────────────────────────────────────────

interface EBState { hasError: boolean; message: string }

class TabErrorBoundary extends Component<{ children: ReactNode }, EBState> {
  state: EBState = { hasError: false, message: '' };

  static getDerivedStateFromError(err: Error): EBState {
    return { hasError: true, message: err.message };
  }

  componentDidCatch(err: Error, info: ErrorInfo) {
    console.error('[FlowObserver] Tab content error:', err, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="fop-error" role="alert">
          <span>This panel crashed: {this.state.message}</span>
          <button className="fop-error__retry" onClick={() => this.setState({ hasError: false, message: '' })}>
            Retry
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// ── TabContent ────────────────────────────────────────────────────────────────

interface TabContentProps { activeTabId: string | null }

export const TabContent: React.FC<TabContentProps> = ({ activeTabId }) => {
  const tabRuntimeStates = useFlowObserverPanelStore((s) => s.tabRuntimeStates);
  const registry = useFlowObserverPanelStore((s) => s.getRegistry());
  const activateTab = useFlowObserverPanelStore((s) => s.activateTab);

  if (!activeTabId) {
    return (
      <div className="fop-content">
        <div className="fop-empty">
          <span>No panels open. Click + to add a panel.</span>
        </div>
      </div>
    );
  }

  const runtime = tabRuntimeStates[activeTabId];
  const ContentComponent = registry.contentComponents[activeTabId];

  if (!runtime || !ContentComponent) {
    return (
      <div className="fop-content">
        <div className="fop-empty">Panel not registered.</div>
      </div>
    );
  }

  const handleRetry = () => activateTab(activeTabId);

  const props: FlowObserverTabContentProps = {
    tabId: activeTabId,
    loadState: runtime.loadState,
    error: runtime.error,
    onRetry: handleRetry,
  };

  return (
    <div
      className="fop-content"
      role="tabpanel"
      id={`fop-tabpanel-${activeTabId}`}
      aria-labelledby={`fop-tab-${activeTabId}`}
      aria-busy={runtime.loadState === 'loading'}
    >
      <TabErrorBoundary>
        <ContentComponent {...props} />
      </TabErrorBoundary>
    </div>
  );
};
